import { useMemo } from 'react'
import { Button, CircularProgress, Typography } from '@mui/material'
import { showSuccessSnackbar } from '@/utils/snackbars'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import { BulkEditDialogProps } from './BulkEditValueDialog'
import useBulkChangeSet from './useBulkChangeSet'

export default function RemoveManualValuesDialog({
  overlay,
  field,
  selectedRows,
  workflowId,
  workflowStateId,
  listQueryKey,
  setPendingEdits,
}: BulkEditDialogProps) {
  const { fieldsMap } = useProjectContext()

  const { submit, isSaving } = useBulkChangeSet({
    field,
    workflowId,
    workflowStateId,
    listQueryKey,
    setPendingEdits,
    overlay,
    successMessage: (docCount) =>
      `Removing manual values for ${field.name} on ${docCount} document${
        docCount === 1 ? '' : 's'
      }.`,
    errorMessage:
      'Failed to remove some manual values. Please try again later.',
  })

  const parentField = fieldsMap[field.id]?.parentField
  const isTableField = !!parentField
  const tableFieldName = parentField?.name ?? ''

  const numberOfDocuments = useMemo(
    () => new Set(selectedRows.map((row) => row.document_id)).size,
    [selectedRows],
  )

  function handleConfirm() {
    const rowsWithManualValues = selectedRows.filter((row) => {
      const existingValue = row.document_row_values?.find(
        (v) => v.project_grid_field_id === field.id,
      )
      return existingValue && existingValue.manual_value !== null
    })

    if (rowsWithManualValues.length === 0) {
      showSuccessSnackbar('No manual values to remove.')
      overlay.close()
      return
    }

    submit(rowsWithManualValues, {
      manual_value: null,
      manual_ref_id: null,
      manual_value_json: null,
    })
  }

  return (
    <Dialog
      title={`Remove Manual Values for ${field.name}`}
      maxWidth="sm"
      {...overlay}
      onClose={() => overlay.close()}
    >
      <DialogContent>
        <Typography>
          This will remove any manual overrides on <strong>{field.name}</strong>{' '}
          for{' '}
          {isTableField ? (
            <>
              <strong>
                {selectedRows.length} {tableFieldName} row
                {selectedRows.length === 1 ? '' : 's'}
              </strong>{' '}
              across{' '}
              <strong>
                {numberOfDocuments} document
                {numberOfDocuments === 1 ? '' : 's'}
              </strong>
            </>
          ) : (
            <>
              <strong>
                {numberOfDocuments} selected document
                {numberOfDocuments === 1 ? '' : 's'}
              </strong>
            </>
          )}
          , reverting them to their original extracted or calculated values.
          Manual values are values entered by a user. This action cannot be
          undone.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Only rows with a manual value will be affected. Rows that have not
          been manually edited will remain unchanged.
        </Typography>
      </DialogContent>
      <DialogFooter>
        <Button
          variant="text"
          onClick={() => overlay.close()}
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={16} /> : undefined}
        >
          Remove Manual Values
        </Button>
      </DialogFooter>
    </Dialog>
  )
}
